/*!
 * Copyright 2023 Quisitive Technologies, LLC. All rights reserved.
 */
import * as d3 from 'd3';
import $ from 'jquery';

const palettes = {
  purple: {
    outline: '#7455FF',
    gradient: {
      start: '#e040fb',
      stop: '#7455ff'
    },
    dotFill: '#E148FB'
  },
  green: {
    outline: '#A9EFC2',
    gradient: {
      start: '#A9EFC2',
      stop: '#957EEF'
    },
    dotFill: '#9F85ED'
  },
  orange: {
    outline: '#FF8874',
    gradient: {
      start: '#FF8874',
      stop: '#BAECDE'
    },
    dotFill: '#BEE5D7'
  }
};

let svg = null;
const divId = null;
let palette = null;
let hasIcons = null;
let icons = null;
let arc = null;
let pie = null;
let outerArc = null;
let outerArc1 = null;
let outerArc2 = null;
let outerArcWalking = null;
let width = null;
let radius = null;
let parentNode = null

export const createDonut = function (settings) {
  const path = settings.path;

  const parent = d3.select('#' + settings.el);
  const margin = settings.margin;
  const paletteName = settings.palette;
  palette = palettes[settings.palette];
  hasIcons = settings.hasIcons;
  icons = settings.icons;
  const divId = settings.el;

  const svgIcons = [];

  svg = d3
    .select('#' + divId)
    .classed('donutChart', true)
    .append('svg')
    .append('g');

  svg.append('g').attr('class', 'labels');
  svg.append('g').attr('class', 'legends');
  svg.append('g').attr('class', 'values');
  svg.append('g').attr('class', 'lines');
  svg.append('g').attr('class', 'circles');
  svg.append('g').attr('class', 'icons');
  svg.append('g').attr('class', 'donutSlices');

  svg
    .append('text')
    .attr('class', 'centerValue')
    .attr('x', 0)
    .attr('y', 0)
    .attr('fill', '#000')
    .attr('text-anchor', 'middle')
    .attr('font-family', 'GTAM')
    .attr('font-size', '24px')
    .text('hello');

  svg
    .append('text')
    .attr('class', 'centerLabel')
    .attr('x', 0)
    .attr('y', 40)
    .attr('fill', '#000')
    .attr('text-anchor', 'middle')
    .attr('font-family', 'GTAM')
    .attr('font-size', '24px')
    .text('hello');

  parentNode = svg.node()

  pie = d3.layout
    .pie()
    .sort(null)
    .value(function (d) {
      return d.value;
    });

  const bbox = parent.node().getBoundingClientRect();
  width = bbox.width - margin.left - margin.right;
  const height = bbox.height - margin.top - margin.bottom;
  radius = 0.3 * width;

  arc = d3.svg
    .arc()
    .outerRadius(radius * 0.8)
    .innerRadius(radius * 0.65);

  outerArc = d3.svg
    .arc()
    .innerRadius(radius * 0.8)
    .outerRadius(radius * 0.9)

  outerArc1 = d3.svg
    .arc()
    .innerRadius(radius * 1.6)
    .outerRadius(radius * 0.9)

  outerArc2 = d3.svg
    .arc()
    .innerRadius(radius * 1.6)
    .outerRadius(radius * 0.9)

  outerArcWalking = d3.svg
    .arc()
    .innerRadius(radius * 1.7)
    .outerRadius(radius * 0.9)

  svg.attr(
    'transform',
    'translate(' +
    (margin.left + width / 2) +
    ',' +
    (margin.top + height / 2) +
    ')'
  );

  // Gradient background
  const svgDefs = svg.append('defs');
  const gradient = svgDefs
    .append('linearGradient')
    .attr('id', divId + '_gradient');

  gradient
    .append('stop')
    .attr('class', 'left ' + paletteName)
    .attr('offset', '0');

  gradient
    .append('stop')
    .attr('class', 'right ' + paletteName)
    .attr('offset', '1');

  const promises = [];

  // Load dot background
  const def1 = new $.Deferred();
  d3.xml(path + '/images/dots_purple.svg')
    .mimeType('image/svg+xml')
    .get(function (error, xml) {
      if (error) {
        throw error;
      } else {
        const svgNode = xml.documentElement.getElementsByTagName('g')[2]
        svgNode.setAttribute('id', divId + '_dots')

        // DOM node
        svg.node().appendChild(svgNode)
        d3
          .select('#' + divId + '_dots')
          .attr('width', 885)
          .attr('fill', palette.dotFill)
          .attr('transform', 'scale(0.91) translate(-252, -252)')

        def1.resolve('resolved');
      }
    });

  promises.push(def1);
  return $.when.apply(undefined, promises).promise();
};

export const updateDonut = function (data) {
  // var = this

  /* ------- CENTER LABEL ------- */
  svg.select('.centerValue').text(data.centerValue.value);
  svg.select('.centerLabel').text(data.centerValue.label);

  /* ------- PIE SLICES ------- */
  svg.select('.donutSlices').each(function () {
    parentNode.appendChild(this);
  });
  svg.select('.donutSlices').moveToBack();
  d3.select('#' + divId + '_dots').moveToBack();

  const slice = svg
    .select('.donutSlices')
    .selectAll('path.slice')
    .data(pie(data.chartData), function (d) {
      return d.data.legend;
    });

  slice
    .enter()
    .insert('path')
    .style('fill', function (d, i) {
      let fill;
      switch (i) {
        case 0: // slice 0
          fill = '#5bc2e7';
          break;
        case 1: // slice 1
          fill = '#e45dbf'; // '
          break;
        case 2: // slice 2
          fill = '#8b84d7';

          break;
      }
      return fill;
    })
    .style('stroke', function (d, i) {
      let stroke;
      switch (i) {
        case 0: // slice 0
          stroke = palette.outline;
          break;
        case 1: // slice 1
          stroke = 'none';
          break;
        case 2: // slice 2
          stroke = 'url(#' + divId + '_gradient)';
          break;
      }
      return stroke;
    })
    .style('z-index', '-100')
    .attr('id', function (d) {
      const id = 'slice_' + d.startAngle;
      return id;
    })
    .attr('class', 'slice');

  slice
    .transition()
    .duration(1000)
    .attrTween('d', function (d) {
      this._current = this._current || d;
      const interpolate = d3.interpolate(this._current, d);
      this._current = interpolate(0);
      return function (t) {
        return arc(interpolate(t));
      };
    });
  slice.exit().remove();

  /* ------- LEGENDS ------- */
  const text2 = svg
    .select('.legends')
    .selectAll('text')
    .data(pie(data.chartData), function (d) {
      return d.data.legend;
    });

  text2
    .enter()
    .append('text')
    .attr('dy', '-56px')
    .attr('fill', '#000')
    .attr('font-family', 'GTAR')
    .attr('font-size', '16px')
    .text(function (d) {
      return d.data.legend;
    });

  function midAngle(d) {
    return d.startAngle + (d.endAngle - d.startAngle) / 2;
  }

  text2
    .transition()
    .duration(1000)
    .attrTween('transform', function (d) {
      this._current = this._current || d;
      const interpolate = d3.interpolate(this._current, d);
      this._current = interpolate(0);
      if (d.data.legend === 'Moderate') {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArc1.centroid(d2)
          pos[0] = midAngle(d2) < Math.PI ? width / 2 : -width / 2
          return 'translate(' + pos + ')'
        }
      } else if (d.data.legend === 'Walking') {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArcWalking.centroid(d2)
          pos[0] = midAngle(d2) < Math.PI ? width / 2 : -width / 2
          return 'translate(' + pos + ')'
        }
      } else {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArc.centroid(d2)
          pos[0] = midAngle(d2) < Math.PI ? width / 2 : -width / 2
          return 'translate(' + pos + ')'
        }
      }
    })
    .styleTween('text-anchor', function (d) {
      this._current = this._current || d;
      const interpolate = d3.interpolate(this._current, d);
      this._current = interpolate(0);
      return function (t) {
        const d2 = interpolate(t);
        return midAngle(d2) < Math.PI ? 'end' : 'start';
      };
    });

  text2.exit().remove();

  text2.attr('visibility', function (d) {
    return d.data.value > 0 ? 'visible' : 'hidden';
  });
  /* ------- TEXT LABELS ------- */
  const text = svg
    .select('.labels')
    .selectAll('text')
    .data(pie(data.chartData), function (d) {
      return d.data.legend;
    });

  text
    .enter()
    .append('text')
    .attr('dy', '30px')
    .attr('fill', '#000')
    .attr('font-family', 'GTAR')
    .attr('font-size', '16px')

    .text(function (d) {
      return d.data.label;
    });

  text
    .transition()
    .duration(1000)
    .text(function (d, i) {
      return d.data.label;
    })
    .attrTween('transform', function (d) {
      this._current = this._current || d;
      const interpolate = d3.interpolate(this._current, d);
      this._current = interpolate(0);
      if (d.data.legend === 'Moderate') {
        if (d.data.value > 30) {
          return function (t) {
            const d2 = interpolate(t)
            const pos = outerArc2.centroid(d2)
            pos[0] = width / 2 * (midAngle(d2) < Math.PI ? 1 : -1)
            return 'translate(' + pos + ')'
          }
        } else {
          return function (t) {
            const d2 = interpolate(t)
            const pos = outerArc1.centroid(d2)
            pos[0] = width / 2 * (midAngle(d2) < Math.PI ? 1 : -1)
            return 'translate(' + pos + ')'
          }
        }
      } else if (d.data.legend === 'Walking') {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArcWalking.centroid(d2)
          pos[0] = width / 2 * (midAngle(d2) < Math.PI ? 1 : -1)
          return 'translate(' + pos + ')'
        }
      } else {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArc.centroid(d2)
          pos[0] = width / 2 * (midAngle(d2) < Math.PI ? 1 : -1)
          return 'translate(' + pos + ')'
        }
      }
    })
    .styleTween('text-anchor', function (d) {
      this._current = this._current || d;
      const interpolate = d3.interpolate(this._current, d);
      this._current = interpolate(0);
      return function (t) {
        const d2 = interpolate(t);
        return midAngle(d2) < Math.PI ? 'end' : 'start';
      };
    });

  text.exit().remove();

  text.attr('visibility', function (d) {
    return d.data.value > 0 ? 'visible' : 'hidden';
  });

  /* ------- VALUES ------- */
  const text3 = svg
    .select('.values')
    .selectAll('text')
    .data(pie(data.chartData), function (d) {
      return d.data.legend;
    });

  const total = d3.sum(data.chartData, function (d) {
    return d.value;
  });

  text3
    .enter()
    .append('text')
    .attr('dy', '-20px')
    .attr('fill', '#000')
    .attr('font-family', 'GTAR')
    .attr('font-size', '24px');

  text3
    .transition()
    .duration(1000)
    .text(function (d, i) {
      return d3.round(d.data.value, 2) + '% '
    })
    .attrTween('transform', function (d) {
      this._current = this._current || d;
      const interpolate = d3.interpolate(this._current, d);
      this._current = interpolate(0);
      if (d.data.legend === 'Moderate') {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArc1.centroid(d2)
          pos[0] = midAngle(d2) < Math.PI ? width / 2 : -width / 2
          return 'translate(' + pos + ')'
        }
      } else if (d.data.legend === 'Walking') {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArcWalking.centroid(d2)
          pos[0] = midAngle(d2) < Math.PI ? width / 2 : -width / 2
          return 'translate(' + pos + ')'
        }
      } else {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArc.centroid(d2)
          pos[0] = midAngle(d2) < Math.PI ? width / 2 : -width / 2
          return 'translate(' + pos + ')'
        }
      }
    })
    .styleTween('text-anchor', function (d) {
      this._current = this._current || d;
      const interpolate = d3.interpolate(this._current, d);
      this._current = interpolate(0);
      return function (t) {
        const d2 = interpolate(t);
        return midAngle(d2) < Math.PI ? 'end' : 'start';
      };
    });

  text3.exit().remove();

  text3.attr('visibility', function (d) {
    return d.data.value > 0 ? 'visible' : 'hidden';
  });

  /* ------- END LEGENDS AND VALUES ------- */

  /* ------- SLICE TO TEXT POLYLINES ------- */

  const polyline = svg
    .select('.lines')
    .selectAll('polyline')
    .data(pie(data.chartData), function (d) {
      return d.data.legend;
    });

  polyline
    .enter()
    .append('polyline')
    .style('stroke', 'black')
    .style('stroke-width', '2')
    .style('opacity', '1');

  polyline
    .transition()
    .duration(1000)
    .attrTween('points', function (d) {
      this._current = this._current || d;
      const interpolate = d3.interpolate(this._current, d);
      this._current = interpolate(0);
      if (d.data.legend === 'Moderate') {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArc1.centroid(d2)
          pos[0] = width / 2 * (midAngle(d2) < Math.PI ? 1 : -1)
          return [arc.centroid(d2), outerArc1.centroid(d2), pos]
        }
      } else if (d.data.legend === 'Walking') {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArcWalking.centroid(d2)
          pos[0] = width / 2 * (midAngle(d2) < Math.PI ? 1 : -1)
          return [arc.centroid(d2), outerArcWalking.centroid(d2), pos]
        }
      } else {
        return function (t) {
          const d2 = interpolate(t)
          const pos = outerArc.centroid(d2)
          pos[0] = width / 2 * (midAngle(d2) < Math.PI ? 1 : -1)
          return [arc.centroid(d2), outerArc.centroid(d2), pos]
        }
      }
    });

  polyline.exit().remove();

  polyline.attr('visibility', function (d) {
    return d.data.value > 0 ? 'visible' : 'hidden';
  });

  /* ------- BLACK CIRCLES ------- */
  const blackCircle = svg
    .select('.circles')
    .selectAll('circle')
    .data(pie(data.chartData), function (d) {
      return d.data.legend;
    })

  blackCircle
    .enter()
    .append('circle')
    .attr('r', '6px')
    .style('stroke', 'black')
    .style('stroke-width', '1px')
    .style('fill', 'none')
    .moveToFront()

  blackCircle
    .transition()
    .duration(1000)
    .attrTween('transform', function (d) {
      this._current = this._current || d;
      const interpolate = d3.interpolate(this._current, d);
      this._current = interpolate(0);
      return function (t) {
        const d2 = interpolate(t);
        const pos = outerArc.centroid(d2);
        pos[0] = radius * 0.95 * (midAngle(d2) < Math.PI ? 1 : -1);
        return 'translate(' + arc.centroid(d2) + ')';
      };
    });

  blackCircle.exit().remove();

  blackCircle.attr('visibility', function (d) {
    return d.data.value > 0 ? 'visible' : 'hidden';
  });
};

d3.selection.prototype.moveToFront = function () {
  return this.each(function () {
    parentNode.appendChild(this)
  })
}

d3.selection.prototype.moveToBack = function () {
  return this.each(function () {
    const firstChild = parentNode.firstChild
    if (firstChild) {
      parentNode.insertBefore(this, firstChild)
    }
  })
}
